import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../contractorApi';
import { storageBaseUrl } from '../../api';

// ── ステータス定義 ────────────────────────────────────────────────
const STATUS_META = {
    chatting:            { label: 'すり合わせ中',   bg: '#e3f2fd', color: '#1565c0' },
    estimated:           { label: '見積提出済み',   bg: '#fff3e0', color: '#f57c00' },
    decided:             { label: '受注確定',       bg: '#e8f5e9', color: '#2e7d32' },
    invoiced:            { label: '請求書提出済み', bg: '#fff3e0', color: '#f57c00' },
    paid:                { label: '入金確認',       bg: '#e8f5e9', color: '#2e7d32' },
    scheduled:           { label: '日程確定',       bg: '#e8eaf6', color: '#283593' },
    in_progress:         { label: '工事中',         bg: '#e0f2f1', color: '#00695c' },
    completion_notified: { label: '完了通知済み',   bg: '#e8f5e9', color: '#388e3c' },
    completed:           { label: '完了',           bg: '#e0e4ea', color: '#6b7280' },
    lost:                { label: '失注',           bg: '#f5f5f5', color: '#9ca3af' },
};

const markSeen = (projectId) => {
    const seen = JSON.parse(localStorage.getItem('contractor_seen_projects') || '[]');
    if (!seen.includes(projectId)) {
        localStorage.setItem('contractor_seen_projects', JSON.stringify([...seen, projectId]));
    }
};

// ── アイコン ──────────────────────────────────────────────────────
const ArrowLeft = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);
const PhoneIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.44 2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);
const MailIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);
const MapPinIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);
const FileTextIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);
const MessageCircleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

// ── セクションカード ──────────────────────────────────────────────
const Card = ({ children, style }) => (
    <div style={{ background: '#fff', borderRadius: 8, padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, ...style }}>
        {children}
    </div>
);

const CardTitle = ({ children }) => (
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' }}>{children}</h2>
);

const InfoRow = ({ icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <span style={{ marginTop: 2 }}>{icon}</span>
        <span style={{ fontSize: '0.9rem', color: '#1a1a2e' }}>{children}</span>
    </div>
);

// ── メイン ────────────────────────────────────────────────────────
const ContractorProjectPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);

    const load = () => {
        api.get(`projects/${projectId}`).then(r => setProject(r.data)).catch(console.error);
    };

    useEffect(() => {
        markSeen(Number(projectId));
        load();
    }, [projectId]);

    const handleAction = async (action) => {
        if (action === 'estimate')   navigate(`/contractor/estimate/create/${projectId}`);
        else if (action === 'invoice') navigate(`/contractor/invoice/create/${projectId}`);
        else if (action === 'schedule') navigate(`/contractor/schedule/${projectId}`);
        else if (action === 'start') {
            await api.patch(`projects/${projectId}/status`, { status: 'in_progress' });
            load();
        } else if (action === 'complete') {
            await api.post(`projects/${projectId}/notify_completion`);
            load();
        }
    };

    if (!project) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const status = project.status;
    const meta = STATUS_META[status] || { label: status, bg: '#e0e4ea', color: '#6b7280' };
    const customer = project.customer;
    const condition = project.condition;

    // 次のアクション判定
    const nextAction = (() => {
        if (['chatting', 'decided', 'estimated'].includes(status) && !project.estimate)
            return { label: '見積書を提出する', action: 'estimate' };
        if (status === 'decided' && !project.invoice)
            return { label: '請求書を提出する', action: 'invoice' };
        if (status === 'paid' && !project.schedule)
            return { label: '工事日程を登録する', action: 'schedule' };
        if (status === 'scheduled')
            return { label: '工事開始を記録する', action: 'start' };
        if (status === 'in_progress')
            return { label: '完了通知を送る', action: 'complete' };
        return null;
    })();

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8', paddingBottom: 48 }}>
            <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 0' }}>

                {/* 戻るボタン */}
                <button
                    onClick={() => navigate(-1)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#005c53', fontWeight: 500, fontSize: '0.9rem', marginBottom: 20, padding: 0 }}
                >
                    <ArrowLeft /> 戻る
                </button>

                {/* ステータスバナー */}
                <div style={{ background: meta.bg, borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: meta.color }}>{meta.label}</span>
                    <span style={{ fontSize: '0.82rem', color: '#6b7280', marginLeft: 4 }}>
                        {customer ? `${customer.last_name} ${customer.first_name} 様` : ''}
                    </span>
                </div>

                {/* 顧客情報 */}
                {customer && (
                    <Card>
                        <CardTitle>顧客情報</CardTitle>
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>顧客名</div>
                            <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{customer.last_name} {customer.first_name} 様</div>
                        </div>
                        {customer.phone && (
                            <InfoRow icon={<PhoneIcon />}>
                                <a href={`tel:${customer.phone}`} style={{ color: '#1a1a2e', textDecoration: 'none' }}>{customer.phone}</a>
                            </InfoRow>
                        )}
                        {customer.email && (
                            <InfoRow icon={<MailIcon />}>
                                <a href={`mailto:${customer.email}`} style={{ color: '#1a1a2e', textDecoration: 'none' }}>{customer.email}</a>
                            </InfoRow>
                        )}
                        {(customer.prefecture || customer.city || customer.address) && (
                            <InfoRow icon={<MapPinIcon />}>
                                {customer.postal_code && `〒${customer.postal_code} `}
                                {customer.prefecture}{customer.city}{customer.address}
                            </InfoRow>
                        )}
                    </Card>
                )}

                {/* 工事条件 */}
                {condition && (
                    <Card>
                        <CardTitle>工事内容</CardTitle>

                        {/* 工事種別 */}
                        {condition.work_types?.length > 0 && (
                            <div style={{ marginBottom: 18 }}>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 8 }}>工事種別</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {condition.work_types.map((t, i) => (
                                        <span key={i} style={{ padding: '4px 12px', background: '#e6f2f1', color: '#005c53', borderRadius: 20, fontSize: '0.83rem', fontWeight: 500 }}>
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 詳細フィールド */}
                        {[
                            { label: '場所', value: [condition.location_prefecture, condition.location_city].filter(Boolean).join(' ') },
                            { label: '幅員', value: condition.construction_width },
                            { label: '希望時期', value: condition.desired_period },
                            { label: 'きっかけ', value: condition.construction_motive },
                            { label: '知ったきっかけ', value: condition.how_found },
                        ].filter(f => f.value).map(f => (
                            <div key={f.label} style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: '0.88rem' }}>
                                <span style={{ color: '#6b7280', minWidth: 100, flexShrink: 0 }}>{f.label}</span>
                                <span style={{ color: '#1a1a2e' }}>{f.value}</span>
                            </div>
                        ))}

                        {/* 現場写真 */}
                        {(condition.panoramic_photo || condition.site_photo) && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 8 }}>現場写真</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    {condition.panoramic_photo && (
                                        <a href={`${storageBaseUrl}/${condition.panoramic_photo}`} target="_blank" rel="noreferrer">
                                            <img src={`${storageBaseUrl}/${condition.panoramic_photo}`} alt="全景写真"
                                                className="site-photo" style={{ objectFit: 'cover', borderRadius: 6 }} />
                                        </a>
                                    )}
                                    {condition.site_photo && (
                                        <a href={`${storageBaseUrl}/${condition.site_photo}`} target="_blank" rel="noreferrer">
                                            <img src={`${storageBaseUrl}/${condition.site_photo}`} alt="現場写真"
                                                className="site-photo" style={{ objectFit: 'cover', borderRadius: 6 }} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 図面 */}
                        {condition.drawing_file && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 8 }}>図面</div>
                                <a
                                    href={`${storageBaseUrl}/${condition.drawing_file}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f5f6f8', borderRadius: 8, textDecoration: 'none', color: '#1a1a2e', fontSize: '0.88rem', fontWeight: 500 }}
                                >
                                    <FileTextIcon /> 図面を開く
                                </a>
                            </div>
                        )}
                    </Card>
                )}

                {/* 見積書サマリー */}
                {project.estimate && (
                    <Card>
                        <CardTitle>見積書</CardTitle>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.88rem', color: '#6b7280' }}>見積金額</span>
                            <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1a2e' }}>¥{project.estimate.amount.toLocaleString()}</span>
                        </div>
                        {project.estimate.note && (
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{project.estimate.note}</p>
                        )}
                    </Card>
                )}

                {/* 請求書サマリー */}
                {project.invoice && (
                    <Card>
                        <CardTitle>請求書</CardTitle>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: '0.88rem', color: '#6b7280' }}>請求金額</span>
                            <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1a2e' }}>¥{project.invoice.amount.toLocaleString()}</span>
                        </div>
                        <span style={{
                            padding: '2px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 500,
                            background: project.invoice.status === 'paid' ? '#e8f5e9' : project.invoice.status === 'transferred' ? '#e3f2fd' : '#fff3e0',
                            color: project.invoice.status === 'paid' ? '#2e7d32' : project.invoice.status === 'transferred' ? '#1565c0' : '#f57c00',
                        }}>
                            {project.invoice.status === 'issued' ? '未払い' : project.invoice.status === 'paid' ? '顧客支払い済み（入金待ち）' : '送金完了'}
                        </span>
                    </Card>
                )}

                {/* 次のアクション */}
                {nextAction && (
                    <Card>
                        <CardTitle>次のアクション</CardTitle>
                        <button
                            onClick={() => handleAction(nextAction.action)}
                            style={{ width: '100%', padding: '14px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
                        >
                            {nextAction.label}
                        </button>
                    </Card>
                )}

                {/* チャットボタン */}
                <button
                    onClick={() => navigate(`/contractor/chat/${projectId}`)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', background: '#fff', color: '#005c53', border: '2px solid #005c53', borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}
                >
                    <MessageCircleIcon /> チャットで話す
                </button>
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .site-photo { width: 140px; height: 100px; }
                @media (min-width: 769px) { .site-photo { width: 240px; height: 170px; } }
            `}</style>
        </div>
    );
};

export default ContractorProjectPage;
